import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  completeLandMission,
  getLandProgress,
  listLandMissions,
  startLandMission,
  type LandMissionAction,
} from '../api/landApi'
import { useAuth } from '../auth/AuthContext'
import {
  LandProgressContext,
  type LandProgressContextValue,
  type LandSyncError,
  type PlayableLandMission,
} from './LandProgressContext'

const PUBLIC_PLAY_MESSAGE = ' 서버 저장과 관계없이 로컬 플레이는 계속할 수 있습니다.'

function statusOf(error: unknown) {
  if (!error || typeof error !== 'object' || !('status' in error)) return null
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' ? status : null
}

function syncError(error: unknown, missionCode?: string): LandSyncError {
  switch (statusOf(error)) {
    case 401:
      return {
        code: 'unauthorized',
        message: `로그인 상태가 만료되어 진행상황을 저장하지 못했습니다.${PUBLIC_PLAY_MESSAGE}`,
        missionCode,
      }
    case 403:
      return {
        code: 'forbidden',
        message: `서버 저장 권한이 없어 이번 진행은 로컬에서만 유지됩니다.${PUBLIC_PLAY_MESSAGE}`,
        missionCode,
      }
    case 409:
      return {
        code: 'conflict',
        message: '서버 진행 순서와 맞지 않아 최신 진행상황을 다시 불러왔습니다. 미션을 다시 시작한 뒤 완료해 주세요.',
        missionCode,
      }
    case 503:
      return {
        code: 'unavailable',
        message: `진행 저장 서버가 일시적으로 준비되지 않았습니다.${PUBLIC_PLAY_MESSAGE}`,
        missionCode,
      }
    default:
      return {
        code: 'network',
        message: `서버와 진행상황을 동기화하지 못했습니다.${PUBLIC_PLAY_MESSAGE}`,
        missionCode,
      }
  }
}

function missionNotLoaded(missionCode: string): LandSyncError {
  return {
    code: 'mission-not-loaded',
    message: '미션 정보를 아직 불러오지 못했습니다. 미션 목록을 새로고침해 주세요.',
    missionCode,
  }
}

function upsertProgress(current: LandMissionAction[], action: LandMissionAction) {
  const index = current.findIndex((item) => item.missionId === action.missionId)
  if (index < 0) return [...current, action]
  return current.map((item, itemIndex) => itemIndex === index ? action : item)
}

function localAction(
  missionId: number,
  state: 'STARTED' | 'COMPLETED',
  current?: LandMissionAction,
): LandMissionAction {
  if (state === 'STARTED' && current?.state === 'COMPLETED') return current

  const now = new Date().toISOString()
  return {
    missionId,
    state,
    persisted: false,
    startedAt: current?.startedAt ?? now,
    completedAt: state === 'COMPLETED' ? now : null,
  }
}

export function LandProgressProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [missions, setMissions] = useState<PlayableLandMission[]>([])
  const [progress, setProgress] = useState<LandMissionAction[]>([])
  const [syncStatus, setSyncStatus] = useState<LandProgressContextValue['syncStatus']>('idle')
  const [error, setError] = useState<LandSyncError | null>(null)
  const authEpochRef = useRef(0)
  const authSnapshotRef = useRef(isAuthenticated)
  const wasAuthenticatedRef = useRef(isAuthenticated)
  const progressMutationRef = useRef(0)
  const missionsLoadedRef = useRef(false)
  const missionLoadRef = useRef<{
    epoch: number
    request: Promise<PlayableLandMission[]>
  } | null>(null)
  const refreshRequestRef = useRef<{
    epoch: number
    authenticated: boolean
    request: Promise<void>
  } | null>(null)
  const localMissionIdsRef = useRef(new Map<string, number>())
  const nextLocalMissionIdRef = useRef(-1)

  if (authSnapshotRef.current !== isAuthenticated) {
    authSnapshotRef.current = isAuthenticated
    authEpochRef.current += 1
  }

  const missionByCode = useMemo(
    () => new Map(missions.map((mission) => [mission.code, mission])),
    [missions],
  )

  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      // Server progress belongs to the previous member and must never remain
      // visible after logout or an automatic 401 downgrade. Mission state is
      // personalized too, so discard that response alongside progress.
      setMissions([])
      setProgress([])
      setError(null)
      setSyncStatus('idle')
      progressMutationRef.current += 1
      missionsLoadedRef.current = false
      missionLoadRef.current = null
      refreshRequestRef.current = null
    }
    wasAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  const loadMissions = useCallback(async () => {
    const epoch = authEpochRef.current
    if (missionLoadRef.current?.epoch === epoch) return missionLoadRef.current.request

    const request = listLandMissions()
      .then((items) => items.map((mission) => ({ ...mission, playable: true as const })))
    missionLoadRef.current = { epoch, request }

    try {
      const loaded = await request
      if (epoch === authEpochRef.current) {
        missionsLoadedRef.current = true
        setMissions(loaded)
      }
      return loaded
    } finally {
      if (missionLoadRef.current?.request === request) missionLoadRef.current = null
    }
  }, [])

  const refresh = useCallback((): Promise<void> => {
    const epoch = authEpochRef.current
    const existing = refreshRequestRef.current
    if (
      existing?.epoch === epoch &&
      existing.authenticated === isAuthenticated
    ) {
      return existing.request
    }

    const mutation = progressMutationRef.current
    const request = (async () => {
      setSyncStatus('syncing')
      setError(null)

      try {
        await loadMissions()
        if (epoch !== authEpochRef.current) return

        if (!isAuthenticated) {
          setSyncStatus('local')
          return
        }

        const nextProgress = await getLandProgress()
        if (epoch !== authEpochRef.current) return
        if (mutation !== progressMutationRef.current) return
        setProgress(nextProgress.missions)
        setSyncStatus('synced')
      } catch (nextError) {
        if (epoch !== authEpochRef.current) return
        if (mutation !== progressMutationRef.current) return
        setError(syncError(nextError))
        setSyncStatus('degraded')
      }
    })()

    refreshRequestRef.current = {
      epoch,
      authenticated: isAuthenticated,
      request,
    }
    void request.finally(() => {
      if (refreshRequestRef.current?.request === request) {
        refreshRequestRef.current = null
      }
    })
    return request
  }, [isAuthenticated, loadMissions])

  const resolveLocalMissionId = useCallback((missionCode: string) => {
    const existing = localMissionIdsRef.current.get(missionCode)
    if (existing !== undefined) return existing

    const missionId = missionByCode.get(missionCode)?.id ?? nextLocalMissionIdRef.current--
    localMissionIdsRef.current.set(missionCode, missionId)
    return missionId
  }, [missionByCode])

  const resolveMission = useCallback(async (missionCode: string) => {
    const epoch = authEpochRef.current
    const normalizedCode = missionCode.trim()
    let mission = missionByCode.get(normalizedCode)
    if (mission) return mission

    if (!missionsLoadedRef.current) {
      setSyncStatus('syncing')
      setError(null)
      try {
        const loaded = await loadMissions()
        if (epoch !== authEpochRef.current) return null
        mission = loaded.find((item) => item.code === normalizedCode)
        if (mission) return mission
      } catch (nextError) {
        if (epoch !== authEpochRef.current) return null
        setError(syncError(nextError, normalizedCode))
        setSyncStatus('degraded')
        return null
      }
    }

    setError(missionNotLoaded(normalizedCode))
    setSyncStatus('degraded')
    return null
  }, [loadMissions, missionByCode])

  const startMission = useCallback(async (missionCode: string) => {
    const normalizedCode = missionCode.trim()
    if (!normalizedCode) {
      setError(missionNotLoaded(normalizedCode))
      setSyncStatus('degraded')
      return null
    }

    if (!isAuthenticated) {
      const missionId = resolveLocalMissionId(normalizedCode)
      const result = localAction(
        missionId,
        'STARTED',
        progress.find((item) => item.missionId === missionId),
      )
      setProgress((current) => upsertProgress(current, result))
      setError(null)
      setSyncStatus('local')
      return result
    }

    const epoch = authEpochRef.current
    const mission = await resolveMission(normalizedCode)
    if (!mission || epoch !== authEpochRef.current) return null

    setError(null)

    const mutation = ++progressMutationRef.current
    setSyncStatus('syncing')
    try {
      const result = await startLandMission(mission.id)
      if (epoch !== authEpochRef.current) return null
      if (mutation !== progressMutationRef.current) return result
      setProgress((current) => upsertProgress(current, result))
      setSyncStatus(result.persisted ? 'synced' : 'local')
      return result
    } catch (nextError) {
      if (epoch !== authEpochRef.current) return null
      if (mutation !== progressMutationRef.current) return null
      const issue = syncError(nextError, mission.code)
      const fallback = localAction(
        mission.id,
        'STARTED',
        progress.find((item) => item.missionId === mission.id),
      )
      setProgress((current) => upsertProgress(current, fallback))
      setError(issue)
      setSyncStatus('degraded')
      return fallback
    }
  }, [isAuthenticated, progress, resolveLocalMissionId, resolveMission])

  const completeMission = useCallback(async (missionCode: string) => {
    const normalizedCode = missionCode.trim()
    if (!normalizedCode) {
      setError(missionNotLoaded(normalizedCode))
      setSyncStatus('degraded')
      return null
    }

    if (!isAuthenticated) {
      const missionId = resolveLocalMissionId(normalizedCode)
      const result = localAction(
        missionId,
        'COMPLETED',
        progress.find((item) => item.missionId === missionId),
      )
      setProgress((current) => upsertProgress(current, result))
      setError(null)
      setSyncStatus('local')
      return result
    }

    const epoch = authEpochRef.current
    const mission = await resolveMission(normalizedCode)
    if (!mission || epoch !== authEpochRef.current) return null

    setError(null)

    const mutation = ++progressMutationRef.current
    setSyncStatus('syncing')
    try {
      const result = await completeLandMission(mission.id)
      if (epoch !== authEpochRef.current) return null
      if (mutation !== progressMutationRef.current) return result
      setProgress((current) => upsertProgress(current, result))
      setSyncStatus(result.persisted ? 'synced' : 'local')
      return result
    } catch (nextError) {
      if (epoch !== authEpochRef.current) return null
      if (mutation !== progressMutationRef.current) return null
      const issue = syncError(nextError, mission.code)

      if (statusOf(nextError) === 409) {
        try {
          const latest = await getLandProgress()
          if (epoch !== authEpochRef.current) return null
          if (mutation !== progressMutationRef.current) return null
          setProgress(latest.missions)
        } catch {
          // The conflict remains non-fatal even if its follow-up refresh fails.
        }
        if (mutation !== progressMutationRef.current) return null
        setError(issue)
        setSyncStatus('degraded')
        return null
      }

      const fallback = localAction(
        mission.id,
        'COMPLETED',
        progress.find((item) => item.missionId === mission.id),
      )
      setProgress((current) => upsertProgress(current, fallback))
      setError(issue)
      setSyncStatus('degraded')
      return fallback
    }
  }, [isAuthenticated, progress, resolveLocalMissionId, resolveMission])

  const value = useMemo<LandProgressContextValue>(() => ({
    missions,
    progress,
    syncStatus,
    error,
    refresh,
    startMission,
    completeMission,
    clearError: () => setError(null),
  }), [completeMission, error, missions, progress, refresh, startMission, syncStatus])

  return <LandProgressContext.Provider value={value}>{children}</LandProgressContext.Provider>
}
